import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import {
  simplifiedExpenseInvoiceSchema,
  type SimplifiedExpenseInvoiceFormData,
} from '../Validation/simplifiedExpenseInvoiceValidation';
import type { ExpenseInvoiceCompanyLookup } from '../Service/expenseInvoiceCompaniesLookupMapper';
import type { ExpenseInvoiceTagLookup } from '../Service/expenseInvoiceTagsLookupMapper';

interface SimplifiedExpenseInvoiceFormProps {
  defaultValues?: Partial<SimplifiedExpenseInvoiceFormData>;
  companies: ExpenseInvoiceCompanyLookup[];
  tags: ExpenseInvoiceTagLookup[];
  companiesLoading?: boolean;
  tagsLoading?: boolean;
  isLoading?: boolean;
  lookupErrorMessage?: string | null;
  onRetryLookups?: () => void;
  onSubmit: (data: SimplifiedExpenseInvoiceFormData) => void | Promise<void>;
  onCancel: () => void;
  mode: 'create' | 'edit';
}

const CURRENCIES = [
  'PLN',
  // Europe (non-EUR)
  'ALL', 'AMD', 'AZN', 'BAM', 'BGN', 'BYN', 'CHF', 'CZK', 'DKK',
  'GEL', 'HUF', 'ISK', 'MDL', 'MKD', 'NOK', 'RON', 'RSD', 'RUB', 'SEK', 'UAH',
  // Eurozone
  'EUR',
  // North Africa
  'DZD', 'EGP', 'LYD', 'MAD', 'TND',
  // Turkey
  'TRY',
  // Other
  'GBP', 'USD',
];

export const SimplifiedExpenseInvoiceForm: React.FC<SimplifiedExpenseInvoiceFormProps> = ({
  defaultValues,
  companies,
  tags,
  companiesLoading = false,
  tagsLoading = false,
  isLoading = false,
  lookupErrorMessage = null,
  onRetryLookups,
  onSubmit,
  onCancel,
  mode,
}) => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SimplifiedExpenseInvoiceFormData>({
    resolver: yupResolver(simplifiedExpenseInvoiceSchema),
    defaultValues: defaultValues ?? {
      companyId: null,
      issueDate: dayjs().format('YYYY-MM-DD'),
      totalGross: null,
      currency: 'PLN',
      description: '',
      tagIds: [],
    },
  });

  const selectedCurrency = watch('currency');
  const totalGross = watch('totalGross');
  const hasLookupError = Boolean(lookupErrorMessage);
  const isSubmitDisabled = isLoading || companiesLoading || tagsLoading || hasLookupError;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pl">
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">
            {mode === 'create' ? 'Dodaj koszt uproszczony' : 'Edytuj koszt uproszczony'}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
              Anuluj
            </Button>
            <Button type="submit" variant="contained" disabled={isSubmitDisabled}>
              {isLoading ? 'Zapisywanie...' : 'Zapisz'}
            </Button>
          </Stack>
        </Stack>

        <Alert severity="info" sx={{ mb: 3 }}>
          Koszt uproszczony jest dokumentem gross-only. Nie obsługuje pozycji VAT, OCR ani
          rozliczeń z przedpłaty dostawcy.
        </Alert>

        {hasLookupError && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              onRetryLookups ? (
                <Button color="inherit" size="small" onClick={onRetryLookups}>
                  Ponów
                </Button>
              ) : undefined
            }
          >
            {lookupErrorMessage}
          </Alert>
        )}

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Dane dokumentu
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="companyId"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={companies}
                      loading={companiesLoading}
                      disabled={companiesLoading || hasLookupError}
                      value={companies.find((company) => company.id === field.value) ?? null}
                      onChange={(_, value) => field.onChange(value?.id ?? null)}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      getOptionLabel={(option) => option.name}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Firma"
                          required
                          error={!!errors.companyId}
                          helperText={errors.companyId?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <Controller
                  name="issueDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Data dokumentu"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(date) => field.onChange(date ? date.format('YYYY-MM-DD') : '')}
                      format="DD.MM.YYYY"
                      maxDate={dayjs()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: !!errors.issueDate,
                          helperText: errors.issueDate?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Waluta"
                      fullWidth
                      required
                      error={!!errors.currency}
                      helperText={errors.currency?.message}
                    >
                      {CURRENCIES.map((currency) => (
                        <MenuItem key={currency} value={currency}>
                          {currency}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="totalGross"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Kwota brutto"
                      fullWidth
                      required
                      value={field.value ?? ''}
                      onChange={(event) =>
                        field.onChange(
                          event.target.value === '' ? null : Number(event.target.value)
                        )
                      }
                      inputProps={{ step: 0.01 }}
                      error={!!errors.totalGross}
                      helperText={errors.totalGross?.message}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <Controller
                  name="tagIds"
                  control={control}
                  render={({ field }) => {
                    const selectedTagIds = field.value ?? [];

                    return (
                      <Autocomplete
                        multiple
                        options={tags}
                        loading={tagsLoading}
                        disabled={tagsLoading || hasLookupError}
                        value={tags.filter((tag) => selectedTagIds.includes(tag.id))}
                        onChange={(_, value) => field.onChange(value.map((tag) => tag.id))}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.name}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option.id}
                              label={option.name}
                              size="small"
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Tagi (opcjonalnie)"
                            error={!!errors.tagIds}
                            helperText={errors.tagIds?.message || 'Tagi globalne dokumentu kosztowego'}
                          />
                        )}
                      />
                    );
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Opis kosztu
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Opis"
                  fullWidth
                  required
                  multiline
                  minRows={4}
                  error={!!errors.description}
                  helperText={errors.description?.message || 'Opisz zbiorczy koszt dokumentu'}
                  placeholder="np. 45 biletów muzealnych dla grupy"
                />
              )}
            />
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 3, bgcolor: 'secondary.light' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Podsumowanie
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Kwota brutto zostanie zapisana jako koszt końcowy dokumentu.
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 1 }}>
              {typeof totalGross === 'number' && Number.isFinite(totalGross)
                ? `${totalGross.toFixed(2)} ${selectedCurrency}`
                : `0.00 ${selectedCurrency}`}
            </Typography>
          </CardContent>
        </Card>

        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
            Anuluj
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitDisabled}>
            {isLoading ? 'Zapisywanie...' : 'Zapisz'}
          </Button>
        </Stack>
      </Box>
    </LocalizationProvider>
  );
};

export default SimplifiedExpenseInvoiceForm;
